import { Reveal, PageHero, CTABand } from "@/components/Shared";
import { services } from "@/data/content";

export default function Expertise() {
    return (
        <div data-testid="expertise-page">
            <PageHero
                index="03"
                overline="Expertise"
                title="Integrated expertise for complex investigations."
                lead="We undertake investigations across the spectrum of financial, corporate and business fraud—from individual incidents to complex matters involving multiple transactions, entities, individuals, data sources and relationships."
                testId="expertise-hero"
            />

            <section className="blueprint-grid relative overflow-hidden">
                <div className="ambient-glow -left-48 top-40 h-[460px] w-[460px] bg-[#1D4F9C]/20" aria-hidden />
                <div className="ambient-glow -right-48 bottom-24 h-[420px] w-[420px] bg-[#A87B2F]/10" aria-hidden />
                <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                <div className="grid gap-12 lg:grid-cols-[260px_1fr]">
                    {/* Sticky index rail */}
                    <aside className="hidden lg:block">
                        <div className="sticky top-40 border border-[#243650] bg-[#0F1B2E] p-7" data-testid="expertise-index-rail">
                            <p className="font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D9A84E]">
                                Index of Services
                            </p>
                            <nav className="mt-6 space-y-3.5">
                                {services.map((s, i) => (
                                    <a
                                        key={s.id}
                                        href={`#${s.id}`}
                                        data-testid={`rail-link-${s.id}`}
                                        className="group flex items-baseline gap-3 text-sm text-[#9AA7BF] transition-colors duration-300 hover:text-[#5B93E8]"
                                    >
                                        <span className="font-tech text-[10px] text-[#D9A84E]">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="leading-snug">{s.title}</span>
                                    </a>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    {/* Service blocks */}
                    <div className="space-y-6">
                        {services.map((s, i) => (
                            <Reveal key={s.id} delay={0.05}>
                                <article
                                    id={s.id}
                                    data-testid={`service-block-${s.id}`}
                                    className="card-lift group scroll-mt-40 border border-[#243650] bg-[#0F1B2E] p-9 lg:p-12"
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-6">
                                        <div className="flex items-center gap-5">
                                            <span className="outline-numeral font-display text-5xl font-bold">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span className="border border-[#5B93E8]/30 bg-[#16263F] p-3.5">
                                                <s.icon className="h-6 w-6 text-[#5B93E8]" />
                                            </span>
                                        </div>
                                        <span className="font-tech text-[10px] uppercase tracking-[0.28em] text-[#8C97AC]">
                                            Practice Area {String(i + 1).padStart(2, "0")} / 10
                                        </span>
                                    </div>
                                    <h2 className="mt-7 font-display text-2xl font-semibold uppercase tracking-wide text-[#E8ECF4]">
                                        {s.title}
                                    </h2>
                                    {s.tagline && (
                                        <p className="mt-4 font-display text-lg font-medium italic text-[#D9A84E]">
                                            {s.tagline}
                                        </p>
                                    )}
                                    {s.paragraphs?.map((p, j) => (
                                        <p key={j} className="mt-4 max-w-3xl text-base leading-relaxed text-[#9AA7BF]">
                                            {p}
                                        </p>
                                    ))}
                                    {s.groups?.map((g, gi) => (
                                        <div key={gi} className="mt-7" data-testid={`service-group-${s.id}-${gi}`}>
                                            {g.label && (
                                                <p className="font-tech text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D9A84E]">
                                                    {g.label}
                                                </p>
                                            )}
                                            <div className={`flex flex-wrap gap-2.5 ${g.label ? "mt-4" : ""}`}>
                                                {g.items.map((item) => (
                                                    <span
                                                        key={item}
                                                        className="border border-white/10 bg-[#0B1322] px-3.5 py-2 text-[13px] text-[#E8ECF4]"
                                                    >
                                                        {item}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    {s.outro && (
                                        <p className="mt-7 max-w-3xl text-sm leading-relaxed text-[#9AA7BF]">
                                            {s.outro}
                                        </p>
                                    )}
                                    {s.note && (
                                        <p className="mt-7 border-l-[3px] border-[#D9A84E] pl-5 font-display text-base font-medium leading-relaxed text-[#E8ECF4]">
                                            {s.note}
                                        </p>
                                    )}
                                    <div className="mt-7 h-0.5 w-14 bg-[#243650] transition-all duration-500 group-hover:w-24 group-hover:bg-[#A87B2F]" />
                                </article>
                            </Reveal>
                        ))}

                        <Reveal>
                            <p className="border-l-[3px] border-[#D9A84E] pl-6 pt-2 font-display text-xl font-medium leading-relaxed text-[#E8ECF4] sm:text-2xl" data-testid="expertise-doctrine">
                                Investigate. Analyse. Trace.{" "}
                                <span className="text-[#8C97AC]">
                                    — every engagement draws from the full spectrum of the practice.
                                </span>
                            </p>
                        </Reveal>
                    </div>
                </div>
                </div>
            </section>

            <CTABand
                title="Discuss an Investigation in Confidence."
                lead="Forensic investigations. Digital evidence. Financial intelligence. Fraud risk. Due diligence."
                testId="expertise-cta"
            />
        </div>
    );
}
